import React, { useState } from 'react';
import { CFCProps, SCREEN_SIZE } from 'types';
import { connect } from 'react-redux';
import { isIOS, isSafari } from 'react-device-detect';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox,
  Button
} from 'carbon-components-react';
import { DocumentPdf16, PlayFilled32 } from '@carbon/icons-react';
import { useDispatch } from 'react-redux';
import { setShowInterstitialPage } from 'redux/app/actions';

const content = [
  {
    buttonText: 'Find your job site on the map',
    explanation: `On the 'Lodge enquiry' web page, find your job site by entering its address to search. Confirm the location with "Use address as job site". If needed, you can manually map the job site using the map zoom buttons and drawing tools.`
  },
  {
    buttonText: 'Complete the form describing the work',
    explanation: `For your job site, now lodge a request for plans. Do this by completing the form "About this enquiry", which pops up beside the map. When you're done, you'll see a summary of your request with tips on what to expect from whom.`
  },
  {
    buttonText: 'Review each of the plans as they arrive',
    explanation: `Upon lodging an enquiry, we automatically prepare map plans. These are sent to your registered email or displayed on a web page, based on your notification preferences. It's crucial to review and understand the plans to minimise risk of damage or harm during excavation.`
  }
];

const InterstitialModal: React.FC<Props> = ({
  onModalClose,
  isModalOpen,
  screenSize
}) => {
  const dispatch = useDispatch();
  const [activeContent, setActiveContent] = useState<number>(0);
  const [showVideo, setShowVideo] = useState<boolean>(false);

  const handleCheckboxChange = (checked: boolean) => {
    localStorage.setItem('interstitial-modal', checked ? 'hide' : '');
  };

  const isHidden =
    localStorage.getItem('interstitial-modal') === 'hide' ? true : false;

  const handleClickClose = (showVideo: boolean) => {
    if (showVideo) {
      setShowVideo(false);
      return false;
    }
    dispatch(setShowInterstitialPage(false));
    onModalClose();
    return true;
  };

  const smallScreens = [SCREEN_SIZE.SM, SCREEN_SIZE.MD];
  return (
    <>
      <ComposedModal
        className="interstitial-modal"
        data-testid="interstitialModal"
        open={isModalOpen}
        preventCloseOnClickOutside={true}
        onClose={() => handleClickClose(showVideo)}
        size="lg"
      >
        <ModalHeader className="video-header">
          {!showVideo && (
            <>
              <div className="interstitial-modal-header-label">WELCOME</div>
              <div className="interstitial-modal-header-content">
                How to request plans for your job site
              </div>
            </>
          )}
        </ModalHeader>
        {showVideo ? (
          <ModalBody className="video-content">
            <div className="video-container">
              <video controls width="100%" height="100%" autoPlay>
                <source
                  src="https://help.byda.com.au/wp-content/uploads/2021/08/Lodging-Enquiry-New.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </ModalBody>
        ) : (
          <ModalBody>
            <div className="interstitial-modal-content">
              {/* for screen > md */}
              {screenSize && !smallScreens.includes(screenSize) && (
                <div className="interstitial-modal-buttons-images-wrapper">
                  <div className="interstitial-modal-buttons">
                    {content.map((row: any, index: any) => (
                      <div
                        key={index}
                        className={`content-buttons${
                          activeContent === index ? '-active' : ''
                        }`}
                        onClick={() => setActiveContent(index)}
                      >
                        {row.buttonText}
                      </div>
                    ))}
                  </div>
                  <div className="interstitial-modal-image">
                    {content.map((row: any, index: any) => (
                      <div key={index}>
                        <div
                          className={`interstitial-image-container${
                            activeContent !== index ? '-hidden' : ''
                          }`}
                        >
                          <img
                            src={`../interstitial-content-${index}.png`}
                            className={`interstitial-image${
                              activeContent !== index ? '-hidden' : ''
                            }`}
                            alt={row.buttonText}
                          />
                          <img
                            src={`../interstitial-content-sample-tag.png`}
                            className={`interstitial-image-sample-tag`}
                            alt=""
                          />
                        </div>
                        <div
                          className={`interstitial-modal-image-text${
                            activeContent !== index ? '-hidden' : ''
                          }`}
                        >
                          {row.explanation}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* for screen === sm */}
              {screenSize && smallScreens.includes(screenSize) && (
                <div className="interstitial-modal-buttons-images-wrapper-sm">
                  {content.map((row: any, index: any) => (
                    <div key={index} className={`cards`}>
                      <div
                        key={index}
                        className={`content-buttons`}
                        onClick={() => setActiveContent(index)}
                      >
                        {row.buttonText}
                      </div>
                      <div key={index} className="interstitial-modal-image">
                        <img
                          src={`../interstitial-content-${index}.png`}
                          className={`interstitial-image`}
                          alt={row.buttonText}
                        />
                        <img
                          src={`../interstitial-content-sample-tag.png`}
                          className={`interstitial-image-sample-tag`}
                          alt=""
                        />
                      </div>
                      <div className={`interstitial-modal-image-text`}>
                        {row.explanation}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="interstitial-modal-video-section">
                {screenSize && smallScreens.includes(screenSize) && (
                  <div className="video-title">Video guide</div>
                )}
                <div
                  className="video-thumbnail-container"
                  onClick={() => setShowVideo(true)}
                >
                  {screenSize && !smallScreens.includes(screenSize) && (
                    <div className="video-thumbnail-overlay">
                      <div className="video-thumbnail-overlay-title">
                        Video guide
                      </div>
                    </div>
                  )}
                  <div className="video-thumbnail-icon">
                    <PlayFilled32 />
                  </div>
                  <video preload="metadata" width="100%" height="100%">
                    <source
                      src={`https://help.byda.com.au/wp-content/uploads/2021/08/Lodging-Enquiry-New.mp4${
                        isIOS || isSafari ? '#t=0.001' : ''
                      }`}
                      type="video/mp4"
                    />
                  </video>
                </div>
                <Button
                  renderIcon={DocumentPdf16}
                  iconDescription="Download"
                  tooltipPosition="right"
                  kind="tertiary"
                  className="download-button"
                  href="https://www.byda.com.au/wp-content/uploads/2022/05/BYDA-Flyer-for-website-Print-Ready.pdf"
                  target="_blank"
                >
                  Download a guide
                </Button>
              </div>
            </div>
          </ModalBody>
        )}

        {!showVideo && (
          <ModalFooter>
            <div className="interstitial-modal-action-buttons center-align">
              {isModalOpen && (
                <Checkbox
                  defaultChecked={isHidden}
                  labelText={`Don't show again`}
                  id={'dontShowAgainCheckbox'}
                  onChange={(checked: boolean) => handleCheckboxChange(checked)}
                />
              )}
            </div>
            <div className="interstitial-modal-action-buttons">
              <Button
                kind="secondary"
                data-testid="applyChanges"
                onClick={() => handleClickClose(showVideo)}
              >
                Close
              </Button>
            </div>
          </ModalFooter>
        )}
      </ComposedModal>
    </>
  );
};

const connector = connect();

type OwnProps = {
  onModalClose: Function;
  isModalOpen: boolean;
  screenSize: any;
};

type Props = CFCProps<typeof connector> & OwnProps;

InterstitialModal.displayName = 'InterstitialModal';

export default React.memo(connector(InterstitialModal));
